import type { ReactNode } from 'react'

/** Page chrome — title lives in the admin navbar. Optional actions row only. */
export function Page({ actions, children }: { title?: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <div className="space-y-5">
      {actions ? <div className="flex justify-end flex-wrap gap-2">{actions}</div> : null}
      {children}
    </div>
  )
}

/** Base shimmer block */
export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded-xl bg-slate-200/80 ${className}`} aria-hidden />
}

export function TableSkeleton({ rows = 6, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <Card className="!p-0 overflow-hidden">
      <div className="border-b border-primary/10 bg-slate-50/80 px-4 py-3 flex gap-3">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className="h-3 flex-1 max-w-[120px]" />
        ))}
      </div>
      <div className="divide-y divide-primary/5">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex items-center gap-3 px-4 py-3.5">
            <Skeleton className="h-10 w-10 shrink-0 rounded-xl" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-3.5 w-2/3 max-w-xs" />
              <Skeleton className="h-2.5 w-1/3 max-w-[140px]" />
            </div>
            <Skeleton className="h-8 w-16 hidden sm:block" />
            <Skeleton className="h-8 w-20" />
          </div>
        ))}
      </div>
    </Card>
  )
}

export function FormSkeleton() {
  return (
    <div className="grid xl:grid-cols-[minmax(0,1fr)_320px] gap-3 sm:gap-4 items-start">
      <Card className="!p-4 sm:!p-5 space-y-4">
        <div className="flex gap-2">
          <Skeleton className="h-10 w-20" />
          <Skeleton className="h-10 w-36 ml-auto" />
          <Skeleton className="h-10 w-28" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-11 w-full" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-11 w-full" />
          </div>
        </div>
        <div className="space-y-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-24 w-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-72 w-full rounded-2xl" />
        </div>
      </Card>
      <div className="space-y-3">
        <Card className="!p-0 overflow-hidden">
          <Skeleton className="h-14 w-full !rounded-none" />
          <div className="p-4 space-y-3">
            <Skeleton className="aspect-[16/10] w-full rounded-2xl" />
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-11 w-full rounded-2xl" />
          </div>
        </Card>
      </div>
    </div>
  )
}

export function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Skeleton className="h-10 w-20" />
        <Skeleton className="h-10 w-28 ml-auto" />
        <Skeleton className="h-10 w-20" />
      </div>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_300px] gap-4">
        <Card className="space-y-4 !p-5 sm:!p-7">
          <Skeleton className="h-5 w-24 rounded-full" />
          <Skeleton className="h-8 w-4/5 max-w-lg" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-48 w-full rounded-2xl" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </Card>
        <div className="space-y-3">
          <Card className="space-y-3 !p-4">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/5" />
          </Card>
          <Card className="space-y-2 !p-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-20 w-full rounded-xl" />
          </Card>
        </div>
      </div>
    </div>
  )
}

export function ListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <Card className="!p-0 overflow-hidden">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center justify-between gap-3 px-4 py-3 border-b border-primary/5 last:border-0"
        >
          <div className="space-y-2 flex-1 min-w-0">
            <Skeleton className="h-3.5 w-1/2 max-w-[220px]" />
            <Skeleton className="h-2.5 w-1/3 max-w-[140px]" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-16" />
            <Skeleton className="h-9 w-16" />
          </div>
        </div>
      ))}
    </Card>
  )
}

/** Compact form shimmer inside modals / panels while edit data loads */
export function ModalFormSkeleton({ lines = 4 }: { lines?: number }) {
  return (
    <div className="space-y-3 py-1" aria-busy="true" aria-label="Loading">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-3 w-16" />
          <Skeleton className={`w-full rounded-2xl ${i === lines - 1 ? 'h-40' : 'h-11'}`} />
        </div>
      ))}
      <div className="flex justify-end gap-2 pt-2">
        <Skeleton className="h-10 w-20" />
        <Skeleton className="h-10 w-24" />
      </div>
    </div>
  )
}

export function SettingsSkeleton() {
  return (
    <div className="space-y-5">
      <Card className="!p-4 sm:!p-5">
        <div className="grid sm:grid-cols-2 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-11 w-full" />
            </div>
          ))}
          <div className="sm:col-span-2">
            <Skeleton className="h-11 w-36" />
          </div>
        </div>
      </Card>
      <ListSkeleton rows={3} />
    </div>
  )
}

export function PolicySkeleton() {
  return (
    <Card className="!p-4 sm:!p-5 space-y-4">
      <div className="space-y-2">
        <Skeleton className="h-3 w-12" />
        <Skeleton className="h-11 w-full max-w-md" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-72 w-full rounded-2xl" />
      </div>
      <Skeleton className="h-11 w-28" />
    </Card>
  )
}

export function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-36 w-full rounded-3xl" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="space-y-3 !p-4">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-8 w-12" />
          </Card>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="space-y-2 !p-4">
            <Skeleton className="h-10 w-10 rounded-xl" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-20" />
          </Card>
        ))}
      </div>
    </div>
  )
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-card border border-primary/10 bg-white/80 backdrop-blur-xl shadow-card-light p-4 sm:p-5 ${className}`}>
      {children}
    </div>
  )
}

export function Btn({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  disabled,
}: {
  children: ReactNode
  onClick?: () => void
  type?: 'button' | 'submit'
  variant?: 'primary' | 'ghost' | 'danger'
  disabled?: boolean
}) {
  const cls =
    variant === 'primary'
      ? 'bg-accent text-navy-900 shadow-[0_4px_16px_-2px_rgba(255,215,0,0.35)] hover:shadow-btn-glow hover:scale-[1.02]'
      : variant === 'danger'
        ? 'bg-red-600 text-white hover:bg-red-500'
        : 'bg-white/90 border border-primary/15 text-primary hover:bg-primary/[0.06]'
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-1.5 rounded-2xl px-3.5 py-2 text-sm font-semibold transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100 min-h-[40px] ${cls}`}
    >
      {children}
    </button>
  )
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-sm space-y-1.5">
      <span className="font-medium text-slate-700">{label}</span>
      {children}
    </label>
  )
}

export function Modal({
  title,
  children,
  onClose,
  size = 'md',
  footer,
}: {
  title: string
  children: ReactNode
  onClose: () => void
  size?: 'md' | 'lg' | 'xl'
  footer?: ReactNode
}) {
  const maxW = size === 'xl' ? 'max-w-4xl' : size === 'lg' ? 'max-w-3xl' : 'max-w-2xl'
  return (
    <div className="fixed inset-0 z-50 grid place-items-end sm:place-items-center p-0 sm:p-4 bg-navy-900/40 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} aria-hidden />
      <div
        className={`relative flex w-full ${maxW} max-h-[92dvh] sm:max-h-[90vh] flex-col overflow-hidden rounded-t-3xl sm:rounded-card border border-primary/10 bg-white shadow-card-hover`}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-primary/10 px-5 py-4 sm:px-6">
          <h2 className="font-display text-lg font-bold text-navy-900 pr-2">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-xl px-2.5 py-1.5 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          >
            Close
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6 space-y-4">{children}</div>
        {footer && (
          <div className="shrink-0 border-t border-primary/10 bg-slate-50/80 px-5 py-3 sm:px-6">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

export const inputClass =
  'w-full rounded-2xl border border-primary/15 bg-white/90 px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-primary/40 focus:ring-2 focus:ring-accent/35'
export const areaClass =
  'w-full rounded-2xl border border-primary/15 bg-white/90 px-3.5 py-2.5 text-sm font-mono text-slate-900 outline-none transition focus:border-primary/40 focus:ring-2 focus:ring-accent/35 min-h-[160px]'
